// =============================================================================
// components/KpiIncidents/IncidentChart.jsx
//
// Grafico de evolucao do KPI com banda PREENCHIDA P5-P95 (em vez de linhas
// tracejadas isoladas) + linha SLA horizontal + valor real grosso.
//
// Para metodo PERCENTILE: usa P5/P50/P95.
// Para metodo ZSCORE/THRESHOLD: usa Avg ± 2σ.
// =============================================================================
import { Box, Typography, CircularProgress } from '@mui/material';
import {
	ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
	Tooltip, ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import { TECH_COLOR } from '../../constants/kpiConstants';

function fmtDate(d) {
	if (!d) return '';
	try {
		return new Date(d).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
	} catch (e) { return ''; }
}

// Custom tooltip — mais limpo do que o default
function CustomTooltip({ active, payload, label }) {
	if (!active || !payload || !payload.length) return null;
	return (
		<Box sx={{
			bgcolor: 'rgba(255,255,255,0.98)',
			border: '1px solid #e5e7eb',
			borderRadius: 1, p: 1, boxShadow: 2,
			fontSize: '0.75rem', minWidth: 140,
		}}>
			<Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
				{label}
			</Typography>
			{payload.map(function (p) {
				if (p.value == null) return null;
				return (
					<Box key={p.dataKey} sx={{
						display: 'flex', justifyContent: 'space-between', gap: 1.5,
						color: p.color,
					}}>
						<span>{p.name}</span>
						<Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
							{Number(p.value).toFixed(3)}
						</Box>
					</Box>
				);
			})}
		</Box>
	);
}

export default function IncidentChart({ incident, history, loading, height }) {
	const chartHeight = height || 240;

	if (loading) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: chartHeight, gap: 1, flexDirection: 'column' }}>
				<CircularProgress size={28} />
				<Typography variant="caption" color="text.secondary">A carregar historico</Typography>
			</Box>
		);
	}

	if (!history || history.length === 0) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: chartHeight }}>
				<Typography variant="body2" color="text.secondary">
					Sem historico disponivel.
				</Typography>
			</Box>
		);
	}

	const techColor = TECH_COLOR[incident.tech] || '#6366f1';
	const method    = incident.detectionMethod || 'PERCENTILE';

	// Preparar dados — para AreaChart precisamos do "delta" do baseline superior - inferior
	const chartData = history.map(function (h) {
		const obj = Object.assign({}, h);
		obj.dateLabel = fmtDate(h.date);

		if (method === 'PERCENTILE') {
			// banda inferior = P5, delta = P95 - P5 (para Area stacked)
			obj.bandLow  = h.p5Baseline;
			obj.bandHigh = h.p95Baseline;
		} else {
			// ZSCORE: Avg ± 2σ
			if (h.avgBaseline != null && h.stdevBaseline != null) {
				obj.bandLow  = h.avgBaseline - 2 * h.stdevBaseline;
				obj.bandHigh = h.avgBaseline + 2 * h.stdevBaseline;
				obj.median   = h.avgBaseline;
			}
		}

		return obj;
	});

	return (
		<Box>
			<ResponsiveContainer width="100%" height={chartHeight}>
				<ComposedChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
					<defs>
						<linearGradient id={'bandGrad-' + incident.incidentId} x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%"   stopColor="#94a3b8" stopOpacity={0.18} />
							<stop offset="100%" stopColor="#94a3b8" stopOpacity={0.06} />
						</linearGradient>
						<linearGradient id={'lineGrad-' + incident.incidentId} x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%"   stopColor={techColor} stopOpacity={1} />
							<stop offset="100%" stopColor={techColor} stopOpacity={0.85} />
						</linearGradient>
					</defs>

					<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
					<XAxis
						dataKey="dateLabel"
						tick={{ fontSize: 10, fill: '#94a3b8' }}
						stroke="#e5e7eb"
						tickLine={false}
					/>
					<YAxis
						tick={{ fontSize: 10, fill: '#94a3b8' }}
						stroke="#e5e7eb"
						tickLine={false}
						width={40}
					/>
					<Tooltip content={<CustomTooltip />} />
					<Legend
						iconSize={10}
						wrapperStyle={{ fontSize: '0.7rem', paddingTop: 4 }}
						iconType="line"
					/>

					{/* SLA threshold — linha horizontal vermelha */}
					<ReferenceLine
						y={incident.slaThreshold}
						stroke="#dc2626"
						strokeDasharray="5 3"
						strokeWidth={1.25}
						label={{
							value: 'SLA ' + Number(incident.slaThreshold).toFixed(2),
							position: 'insideTopRight',
							fontSize: 9,
							fill: '#dc2626',
						}}
					/>

					{/* Banda inferior (transparente, para baseline da Area) */}
					<Area
						type="monotone"
						dataKey="bandLow"
						stroke="none"
						fill="transparent"
						stackId="band"
						legendType="none"
						name="band-low-helper"
					/>
					{/* Banda principal preenchida (delta) */}
					<Area
						type="monotone"
						dataKey="bandHigh"
						stroke="#cbd5e0"
						strokeDasharray="3 3"
						strokeWidth={1}
						fill={'url(#bandGrad-' + incident.incidentId + ')'}
						stackId="band"
						name={method === 'PERCENTILE' ? 'Banda P5-P95' : 'Banda Avg ± 2σ'}
					/>

					{/* Mediana / Avg */}
					{method === 'PERCENTILE' ? (
						<Line type="monotone" dataKey="p50Baseline"
							name="Mediana (P50)"
							stroke="#64748b" strokeDasharray="4 2" strokeWidth={1.25}
							dot={false} activeDot={false} />
					) : (
						<Line type="monotone" dataKey="avgBaseline"
							name="Media"
							stroke="#64748b" strokeDasharray="4 2" strokeWidth={1.25}
							dot={false} activeDot={false} />
					)}

					{/* Valor real — linha grossa colorida */}
					<Line
						type="monotone"
						dataKey="kpiValue"
						name="Valor real"
						stroke={'url(#lineGrad-' + incident.incidentId + ')'}
						strokeWidth={2.5}
						dot={{ r: 3, fill: techColor, strokeWidth: 0 }}
						activeDot={{ r: 5, fill: techColor, stroke: 'white', strokeWidth: 2 }}
					/>
				</ComposedChart>
			</ResponsiveContainer>
		</Box>
	);
}
